import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import styled, { useTheme } from "styled-components";
import { routeList } from "../App";
import api, { Chat } from "../services/api";
import { useUserStore } from "../store/useUserStore";
import { RootContainer } from "../styles/Container.style";
import { BigTitle, Paragraph } from "../styles/Text";
import { Column, Gap, Row } from "../styles/Utils";

export const hasConvNewMessage = (chat: Chat, userId: string | null) => {
  return chat.messages.length > 0 && chat.messages[0].sender != userId && !chat.messages[0].seenBy.includes(userId!);
};

export default function ChatPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [chats, setChats] = useState<Chat[]>([]);
  const {
    profile: { _id },
    nbConvWithNewMessage,
    setNbConvWithNewMessage,
  } = useUserStore();

  useEffect(() => {
    (async function getChats() {
      try {
        const response = await api.getChats();
        setChats(sortChats(response.data.chats));
      } catch (err) {
        console.log("Error fetching chats", err);
        alert("Error fetching chats");
      }
    })();
  }, []);

  const sortChats = (chats: Chat[]): Chat[] => {
    return chats.sort((a, b) => {
      const dateA = getLastActivityDate(a);
      const dateB = getLastActivityDate(b);

      return dateB.getTime() - dateA.getTime(); // Plus récent en haut
    });
  };

  const getLastActivityDate = (chat: Chat): Date => {
    if (chat.messages.length > 0) {
      return new Date(new Date(chat.messages[0].createdAt));
    }
    return new Date(chat.createdAt);
  };

  const handleChatClick = () => {
    setNbConvWithNewMessage(nbConvWithNewMessage - 1);
  };

  if (chats.length == 0)
    return (
      <Row $justifyContent="center">
        <BeatLoader color={theme.color.primaryDark} />
      </Row>
    );

  return (
    <RootContainer>
      <BigTitle>MESSAGES</BigTitle>
      <Gap $height={1} />
      {chats.map((chat) => {
        const sender = chat?.participants.find((ptcp) => ptcp._id != _id);
        return (
          <Link key={chat._id} to={`${routeList.chatWith}/${chat._id}`} onClick={handleChatClick}>
            <Column $alignItem="center">
              <Divider />
              <RowOvl $gap={1} $flexGrow={1}>
                <Image
                  src={chat.participants[0].imageUrls[0]}
                  onClick={(event: React.MouseEvent<HTMLImageElement>) => {
                    event.preventDefault(); // Prevent Link from operating
                    navigate(`${routeList.profileView}/${sender!._id}`);
                  }}
                />
                <ColumnOvl $justifyContent="space-between" $alignItem="flex-start">
                  <DogName>{chat.participants[0].dogName}</DogName>
                  {chat.messages.length > 0 && <MessagePreview>{chat.messages[0].content}</MessagePreview>}
                </ColumnOvl>
                {chat.newConv && chat.messages.length == 0 && (
                  <Signal $color={theme.color.success}>
                    <span>START</span>
                  </Signal>
                )}
                {hasConvNewMessage(chat, _id) && (
                  <Signal $color={theme.color.tertiary}>
                    <span>NEW</span>
                  </Signal>
                )}
              </RowOvl>
            </Column>
          </Link>
        );
      })}
      <Row $justifyContent="center">
        <Divider />
      </Row>
    </RootContainer>
  );
}

const ColumnOvl = styled(Column)`
  overflow: hidden;
`;

const MessagePreview = styled(Paragraph)`
  text-align: start;
  font-weight: normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block; /* Or inline-block */
  width: 100%;
`;

const DogName = styled.span`
  font-family: ${(props) => props.theme.font.family.label};
  color: ${(props) => props.theme.color.primary};
  font-size: ${(props) => props.theme.font.size.title.xbig};
`;

const Signal = styled(Row)<{ $color: string }>`
  position: absolute;
  right: -1rem;
  width: 9rem;
  height: calc(100% - 2rem);
  border-radius: 100% 0 0 100%;
  background-color: ${(props) => props.$color};
  align-items: center;
  /* padding-inline: 3rem; */
  padding-left: 2rem;
  > span {
    font-family: ${(props) => props.theme.font.family.label};
    font-size: ${(props) => props.theme.font.size.title.big};
    color: ${(props) => props.theme.color.tertiaryLight};
  }
`;

const RowOvl = styled(Row)`
  position: relative;
  width: 100%;
  padding-block: 1rem;
`;

const Image = styled.img`
  border-radius: 100px;
  width: 6rem;
  aspect-ratio: 1;
`;

const Divider = styled.div`
  width: 80%;
  border-radius: 100;
  height: 3px;
  background-color: ${(props) => props.theme.color.primaryDark};
`;
